import clsx from 'clsx';

import { SETTINGS_ACTION_BUTTON, SETTINGS_ACTION_SLOT } from '@/auth/components/shared';
import type { CognitoMfaType } from '@/auth/utils/cognitoSignIn';
import Button from '@/componentsv2/Button';
import InfoTip from '@/componentsv2/InfoTip';

export const FACTOR_LABELS: Record<CognitoMfaType, string> = {
  TOTP: 'Authentication app',
  SMS: 'Text message (SMS)',
  EMAIL: 'Email',
};

/** The label mid-sentence: only the first letter drops case, so SMS stays SMS. */
export function factorPhrase(type: CognitoMfaType): string {
  const label = FACTOR_LABELS[type];
  return label.charAt(0).toLowerCase() + label.slice(1);
}

const FACTORS: CognitoMfaType[] = ['TOTP', 'SMS', 'EMAIL'];

export const ONE_METHOD_NOTE =
  'We support one two-factor method at a time. Turning one on turns off the other.';

interface CognitoMfaFactorListProps {
  enabled: CognitoMfaType[];
  /** One of the user's teams enforces two-factor authentication. */
  teamEnforced: boolean;
  busy?: boolean;
  onSetUp: (type: CognitoMfaType) => void;
  onTurnOff: (type: CognitoMfaType) => void;
}

/** The three Cognito factors with their state and the action each allows. */
export default function CognitoMfaFactorList({
  enabled,
  teamEnforced,
  busy = false,
  onSetUp,
  onTurnOff,
}: CognitoMfaFactorListProps): JSX.Element {
  // The team policy needs at least one factor, so the last one stays on.
  const lastIsRequired = teamEnforced && enabled.length === 1;

  return (
    <div className="flex flex-col gap-12">
      <div className="flex items-center gap-8">
        <span className="text-14 font-bold text-on-surface">Methods</span>
        <InfoTip term="two-factor methods" definition={ONE_METHOD_NOTE} />
      </div>
      <ul className="flex flex-col divide-y divide-surface-border">
        {FACTORS.map((type) => {
          const on = enabled.includes(type);
          return (
            <li key={type} className="flex flex-wrap items-center justify-between gap-16 py-12">
              <div className="flex flex-col">
                <span className="font-bold text-on-surface">{FACTOR_LABELS[type]}</span>
                <span className="text-14 text-on-surface-secondary">{on ? 'On' : 'Off'}</span>
              </div>
              {on ? (
                <Button
                  className={clsx(SETTINGS_ACTION_SLOT, SETTINGS_ACTION_BUTTON)}
                  variant="secondary"
                  size="medium"
                  disabled={busy || lastIsRequired}
                  onClick={() => onTurnOff(type)}
                  aria-label={`Turn off ${FACTOR_LABELS[type]}`}
                >
                  Turn off
                </Button>
              ) : (
                <Button
                  className={clsx(SETTINGS_ACTION_SLOT, SETTINGS_ACTION_BUTTON)}
                  size="medium"
                  disabled={busy}
                  onClick={() => onSetUp(type)}
                  aria-label={`Set up ${FACTOR_LABELS[type]}`}
                >
                  Set up
                </Button>
              )}
            </li>
          );
        })}
      </ul>
      {lastIsRequired && (
        <p className="text-14 text-on-surface-secondary">
          Your team requires two-factor authentication. Set up another method before turning this
          one off.
        </p>
      )}
    </div>
  );
}
